import {
    Avatar,
    Button,
    Grid,
    makeStyles,
    Paper,
    TextField,
    Typography,
} from "@material-ui/core";
import { Formik, Form } from "formik";
import { Link } from "react-router-dom";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppStateType } from "../../../redux/reducers";
import { clearUserError, fetchUser, updateUser } from "../../../redux/actions/userActions";
import { CustomSelect } from "../../../components/customSelect";
import { AppDispatchType } from "../../../redux/store";
import { Notification } from "../../../components/Notification"
import { Color } from "@material-ui/lab";
import { useTranslation } from "react-i18next";
import { Alert } from "@material-ui/lab";

const useStyles = makeStyles((theme) => ({
    root: {
        marginBottom: "20px",
        marginTop: "20px",
        padding: "20px",
        flexGrow: 1,
    },
    large: {
        width: theme.spacing(15),
        height: theme.spacing(15),
        marginBottom: "10px",
        margin: "0 auto",
    },
    marginTop: {
        marginTop: "10px",
    },
    center: {
        width: "100%",
        textAlign: "center",
    },
    button: {
        background: "linear-gradient(90deg, #84FAB0 0%, #8FEAFE 100%)",
        boxShadow: "0px 0px 14px rgba(53, 64, 82, 0.05)",
    },
    feedback: {
        marginTop: theme.spacing(2),
        marginLeft: theme.spacing(4),
        marginRight: theme.spacing(4),
    },
    alert: {
        backgroundColor: "#354557",
        color: "#cfcfcf"
    },
    infoAlert: {
        "& .MuiAlert-icon": {
          color: "#B8E7FB",
        },
        marginTop: "16px",
        backgroundColor: "#26374d",
    }
}));

const positionOptions = [
    "owner",
    "executive",
    "manager",
    "operations_and_production",
    "other",
];

const departmentOptions = [
    "it",
    "marketing",
    "finance",
    "human_resources",
    "sales",
    "operations",
    "other",
];

export function UserInformation() {
    const classes = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [notify, setNotify] = useState({ isOpen: false, message: "", type: "" as Color })
    const [submitted, setSubmitted] = useState(false);
    const error: any = useSelector((state: AppStateType) => state.userReducer.error);
    const [pictureAttached, setPictureAttached] = useState(false);

    const positionMapping = {
        "owner": t("settingsForms_UserInformation_positionOptions_owner"),
        "executive": t("settingsForms_UserInformation_positionOptions_executive"),
        "manager": t("settingsForms_UserInformation_positionOptions_manager"),
        "operations_and_production": t("settingsForms_UserInformation_positionOptions_owner"),
        "other": t("settingsForms_UserInformation_positionOptions_other")
    }

    const departmentMapping = {
        "it": t("settingsForms_UserInformation_departmentOptions_it"),
        "marketing": t("settingsForms_UserInformation_departmentOptions_marketing"),
        "finance": t("settingsForms_UserInformation_departmentOptions_finance"),
        "human_resources": t("settingsForms_UserInformation_departmentOptions_human_resources"),
        "sales": t("settingsForms_UserInformation_departmentOptions_sales"),
        "operations": t("settingsForms_UserInformation_departmentOptions_operations"),
        "other": t("settingsForms_UserInformation_departmentOptions_other"),
    }

    const user: any = useSelector(
        (state: AppStateType) => state.authReducer.user
    );
    const userProfile: any = useSelector(
        (state: AppStateType) => state.userReducer.user
    );
    type UserForm = {
        email: string;
        password: string;
        first_name: string;
        last_name: string;
        position: string;
        department: string;
        picture: File;
        company: string;
        file: any;
    };

    useEffect(() => {
        if (user) {
            dispatch(fetchUser(user.user, user.token));
        }
        return () => {
            dispatch(clearUserError())
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (userProfile) {
            setInitialValues({
                first_name: userProfile.first_name,
                last_name: userProfile.last_name,
                email: userProfile.email,
                department: userProfile.department,
                position: userProfile.position,
            });
        }
    }, [userProfile]);
    const [initialValues, setInitialValues] = useState<any>(null!);
    const getErrorString = useCallback(() => {
        return Object.keys(error).map(key => (error[key][0])).toString()
    }, [error]);
    const onSubmit = async (userPayload: UserForm) => {

        dispatch(clearUserError())

        setNotify({
            ...notify,
            isOpen: false,
        })
        // const { file, ...payload } = userPayload;
        const fd = new FormData();
        (Object.keys(userPayload) as any[]).forEach((key) => fd.append(key, (userPayload as any)[key]))
        
        dispatch(async (dp: AppDispatchType) => {
            await updateUser(user.user, user.token, fd)(dp)
            setNotify({
                isOpen: true,
                message: t("questionUnits_uploadSuccess"),
                type: "success"
            })
            setSubmitted(true);
            setPictureAttached(false)
        });

    };
    if (!initialValues) {
        return <div></div>;
    } else {
        return (
            <Paper className={classes.root}>
                <Typography variant="h4">{t("settingsForms_UserInformation_title")}</Typography>
                <Formik<UserForm>
                    initialValues={initialValues}
                    onSubmit={onSubmit}
                >
                    {({ values, handleChange, setValues, handleSubmit }) => {                        
                        const handleCapture = async (
                            e: React.ChangeEvent<HTMLInputElement>
                        ) => {
                            if (e.target.files) {
                                if (e.target.files[0]) {
                                    const file = e.target.files[0];
                                    setValues({
                                        ...values,
                                        picture: file,
                                        file: URL.createObjectURL(file),
                                    });
                                    setPictureAttached(true);
                                }

                            }
                        };
                        return (
                            <Form onSubmit={handleSubmit}>

                                <Grid container spacing={5} direction="row-reverse">
                                    <Grid item xs={12} sm={4}>
                                        <div className={classes.center} style={{ marginTop: "20px" }}>
                                            <Avatar
                                                alt="Avatar"
                                                className={
                                                    classes.large
                                                }
                                                // component="img"
                                                src={values.file ?? userProfile.picture}
                                            />
                                            <input
                                                id="button-file"
                                                type="file"
                                                style={{
                                                    display: "none",
                                                }}
                                                onChange={handleCapture}
                                            />
                                            <label htmlFor="button-file">
                                                <Button
                                                    variant="contained"
                                                    className={
                                                        classes.button
                                                    }
                                                    component="span"
                                                    style={{
                                                        marginBottom:
                                                            "10px",
                                                    }}
                                                >
                                                    <CloudUploadIcon
                                                        style={{
                                                            paddingRight:
                                                                "0.2em",
                                                        }}
                                                    />{" "}
                                                    {t("settingsForms_UserInformation_UploadPicture")}
                                                </Button>
                                            </label>
                                        </div>
                                        <Typography
                                            align="center"
                                            style={{ fontSize: 11 }}
                                        >
                                            {t("settingsForms_UserInformation_formatRestriction")}
                                        </Typography>
                                        <Button
                                            component={Link}
                                            to="/auth/reset-password"
                                            fullWidth
                                            color="primary"
                                        >
                                            {t("resetPassword_title")}
                                        </Button>
                                    </Grid>
                                    <Grid item xs={12} sm={8}>
                                        <Grid
                                            direction="row"
                                            container
                                            spacing={5}
                                        >

                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    className={
                                                        classes.marginTop
                                                    }
                                                    fullWidth
                                                    label={t("signupForm_signUp_nameLabel")}
                                                    id="first_name"
                                                    value={
                                                        values.first_name
                                                    }
                                                    onChange={handleChange}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    className={
                                                        classes.marginTop
                                                    }
                                                    fullWidth
                                                    label={t("settingsForms_UserInformation_surname")}
                                                    id="last_name"
                                                    value={values.last_name}
                                                    onChange={handleChange}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                className={
                                                    classes.marginTop
                                                }
                                                fullWidth
                                                label={t("signinForm_emailLabel_textfield")}
                                                id="email"
                                                value={values.email}
                                                onChange={handleChange}
                                            />
                                        </Grid>
                                        <Grid
                                            direction="row"
                                            container
                                            spacing={5}
                                        >
                                            <Grid item xs={12} sm={6}>
                                                <CustomSelect
                                                    inputLabel={t("settingsForms_UserInformation_position")}
                                                    value={values.position}
                                                    options={positionOptions}
                                                    getOptionLabel={el => (positionMapping as any)[el] ?? ""}
                                                    onChange={el => {
                                                        setValues({
                                                            ...values,
                                                            position: el!
                                                        })
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <CustomSelect
                                                    inputLabel={t("settingsForms_UserInformation_department")}
                                                    value={values.department}

                                                    getOptionLabel={el => (departmentMapping as any)[el] ?? ""}
                                                    options={departmentOptions}
                                                    onChange={el => {
                                                        setValues({
                                                            ...values,
                                                            department: el!
                                                        })
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                </Grid>

                                {error && (
                                    <div className={classes.feedback}>
                                        {getErrorString()}
                                    </div>
                                )}
                                <Button
                                    variant="contained"
                                    type="submit"
                                    className={classes.button}
                                    style={{ marginTop: "20px" }}
                                    >
                                    {t("generalWords_SaveChanges")}
                                </Button>
                                {pictureAttached ?
                                    <Alert className={classes.infoAlert} severity="info">
                                        {t("settingsPage_alertMsgImageUpload")}
                                    </Alert> : null
                                }
                                {!error && submitted ? <Notification notify={notify} setNotify={setNotify} className={classes.alert} /> : null}
                            </Form>
                        );
                    }}
                </Formik>
            </Paper>
        );
    }
}
